// tableHeader.tsx
import './tableHeader.css';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ListFilter, ListFilterPlus } from 'lucide-react';
import { TableColumnConfig, JsonColumnConfig } from '../tableMain';
import Checkbox from '../../common/checkbox/checkbox';
import TableFilter from '../table_filter/tableFilter';
import { trackEvent } from '../../../contexts/userAnalytics';
import type { TableResetActions } from '../tableMain';

interface TableHeaderProps {
    columnConfigs: TableColumnConfig[];
    jsonColumnConfig?: JsonColumnConfig[];
    columnWidths: number[];
    totalWidth: number;
    minWidth: number;
    checkboxColumnWidth: number;
    selectableRows: boolean;
    hasSelectedRows?: boolean;
    enableFilters: boolean;
    onSelectAll?: () => void;
    data?: Record<string, any>[];
    onFilterStateChange?: (hasFilters: boolean) => void;
    onFiltersChange?: (filters: Record<string, Set<string>>) => void;
    onSortsChange?: (sorts: Record<string, 'asc' | 'desc' | 'none'>) => void;
    initialFilters?: Record<string, Set<string>>;
    initialSorts?: Record<string, 'asc' | 'desc' | 'none'>;
    userData?: any;
    tableName: string;
    resetActionsRef?: React.MutableRefObject<TableResetActions | null>;
}

const TableHeader: React.FC<TableHeaderProps> = ({
    columnConfigs,
    jsonColumnConfig,
    columnWidths,
    totalWidth,
    minWidth,
    checkboxColumnWidth,
    selectableRows,
    hasSelectedRows = false,
    enableFilters,
    onSelectAll,
    data = [],
    onFilterStateChange,
    onFiltersChange,
    onSortsChange,
    initialFilters,
    initialSorts,
    userData,
    tableName,
    resetActionsRef,
}) => {
    const [filterOpen, setFilterOpen] = useState(false);
    const [filterPosition, setFilterPosition] = useState({ top: 0, left: 0 });
    const [activeColumnName, setActiveColumnName] = useState('');
    const [activeFilters, setActiveFilters] = useState<Record<string, Set<string>>>(() => initialFilters ?? {});
    const [activeSorts, setActiveSorts] = useState<Record<string, 'asc' | 'desc' | 'none'>>(() => initialSorts ?? {});

    const headerRef = useRef<HTMLDivElement>(null);
    const pendingFilterEvent = useRef<{ tableName: string; colName: string } | null>(null);

    // Use refs for stable callback references
    const onFilterStateChangeRef = useRef(onFilterStateChange);
    const onFiltersChangeRef = useRef(onFiltersChange);
    const onSortsChangeRef = useRef(onSortsChange);

    // Update refs when callbacks change (but don't cause re-renders)
    useEffect(() => { onFilterStateChangeRef.current = onFilterStateChange; }, [onFilterStateChange]);
    useEffect(() => { onFiltersChangeRef.current = onFiltersChange; }, [onFiltersChange]);
    useEffect(() => { onSortsChangeRef.current = onSortsChange; }, [onSortsChange]);

    // Initialize filters from parent (for initialFilter prop from tableMain)
    useEffect(() => {
        if (initialFilters && Object.keys(initialFilters).length > 0) {
            setActiveFilters(initialFilters);
        }
    }, [initialFilters]);

    const clearAllFilters = useCallback(() => {
        setActiveFilters({});
        setFilterOpen(false);
        onFilterStateChangeRef.current?.(false);
        onFiltersChangeRef.current?.({});
    }, []);
    
    const clearAllSorts = useCallback(() => {
        setActiveSorts({});
        setFilterOpen(false);
        onSortsChangeRef.current?.({});
    }, []);

    // Stable window assignments (only when functions change)
    useEffect(() => {
        if (!resetActionsRef) return;
        const actions: TableResetActions = {
            clearAllFilters,
            clearAllSorts,
        };
        resetActionsRef.current = actions;

        return () => {
            if (resetActionsRef.current === actions) {
                resetActionsRef.current = null;
            }
        };
    }, [resetActionsRef, clearAllFilters, clearAllSorts]);

    // Only notify parent when activeFilters actually changes
    useEffect(() => {
        const hasFilters = Object.keys(activeFilters).length > 0;
        onFilterStateChangeRef.current?.(hasFilters);
        onFiltersChangeRef.current?.(activeFilters);
        console.log('Active filters changed:', activeFilters);
    }, [activeFilters]); // Only activeFilters dependency

    useEffect(() => {
        if (filterOpen) return;

        const pending = pendingFilterEvent.current;
        if (!pending) return;

        pendingFilterEvent.current = null;

        trackEvent('table_filter_apply', {
            tableName: pending.tableName,
            colName: pending.colName,
        });
    }, [filterOpen]);

    const handleFilterApply = useCallback((columnName: string, checkedValues: Set<string>, isActive: boolean) => {
        if (isActive) {
            setActiveFilters(prev => ({
                ...prev,
                [columnName]: checkedValues
            }));
            pendingFilterEvent.current = {
                tableName,
                colName: columnName,
            };
        } else {
            setActiveFilters(prev => {
                const newFilters = { ...prev };
                delete newFilters[columnName];
                return newFilters;
            });
        }
    }, []);

    const handleSortApply = useCallback((columnName: string, sortOrder: 'asc' | 'desc' | 'none') => {
        let newSorts: Record<string, 'asc' | 'desc' | 'none'>;
        
        if (sortOrder === 'none') {
            setActiveSorts(prev => {
                const { [columnName]: _, ...rest } = prev;
                return rest;
            });
            newSorts = { ...activeSorts };
            delete newSorts[columnName];
        } else {
            newSorts = { [columnName]: sortOrder };
            setActiveSorts(newSorts);
        }
        
        onSortsChangeRef.current?.(newSorts);
    }, [activeSorts]);

    const handleFilterClick = useCallback((event: React.MouseEvent, columnName: string) => {
        event.stopPropagation();
        
        const filterIcon = event.currentTarget;
        const headerCell = filterIcon.closest('.tableHeader-cellContainer') as HTMLElement;
        
        if (headerCell) {
            const rect = headerCell.getBoundingClientRect();
            
            setFilterPosition({
                top: rect.bottom + 1.5,
                left: rect.left - 10,
            });
            setActiveColumnName(columnName);
            setFilterOpen(true);
        }
    }, []);

    const getJsonDisplayValue = useCallback((value: any): string => {
        try {
            const jsonData = typeof value === 'string' ? JSON.parse(value) : value;
            if (Array.isArray(jsonData)) {
                return `${jsonData.length} items`;
            } else if (typeof jsonData === 'object') {
                const keys = Object.keys(jsonData);
                return `${keys.length} items`;
            } else {
                return String(value);
            }
        } catch (error) {
            return String(value);
        }
    }, []);

    const getFilteredDataForColumn = useCallback((excludeColumnName: string): Record<string, any>[] => {
        if (Object.keys(activeFilters).length === 0) {
            return data;
        }

        return data.filter(row => {
            for (const [columnName, allowedValues] of Object.entries(activeFilters)) {
                if (columnName === excludeColumnName) {
                    continue;
                }
                
                let cellValue = row[columnName];
                
                const columnConfig = columnConfigs.find(config => config.colName === columnName);
                
                // Apply conditionalDisplay if defined
                if (columnConfig?.conditionalDisplay && userData) {
                    cellValue = columnConfig.conditionalDisplay(cellValue, row, userData);
                }
                
                let stringValue: string;
                const isJsonColumn = columnConfig?.colType === 'json';
                
                if (cellValue === null || cellValue === undefined || cellValue === '') {
                    stringValue = '(Empty)';
                } else if (isJsonColumn) {
                    stringValue = getJsonDisplayValue(cellValue);
                } else {
                    stringValue = String(cellValue);
                }
                
                if (!allowedValues.has(stringValue)) {
                    return false;
                }
            }
            
            return true;
        });
    }, [data, activeFilters, userData]);

    const getColumnData = useCallback((columnName: string): any[] => {
        const filteredData = getFilteredDataForColumn(columnName);
        return filteredData.map(row => row[columnName]);
    }, [getFilteredDataForColumn]);
    
    const getOriginalColumnData = useCallback((columnName: string): any[] => {
        return data.map(row => row[columnName]);
    }, [data]);

    const hasActiveFilter = useCallback((columnName: string): boolean => {
        return activeFilters[columnName] && activeFilters[columnName].size > 0;
    }, [activeFilters]);

    const hasActiveSort = useCallback((columnName: string): boolean => {
        return activeSorts[columnName] && activeSorts[columnName] !== 'none';
    }, [activeSorts]);

    const hasActiveState = useCallback((columnName: string): boolean => {
        return hasActiveFilter(columnName) || hasActiveSort(columnName);
    }, [hasActiveFilter, hasActiveSort]);

    const getColumnIcon = useCallback((columnName: string) => {
        if (hasActiveState(columnName)) {
            return (
                <ListFilterPlus
                    size={15}
                    style={{ color: 'var(--color-blue-default)' }}
                />
            );
        } else {
            return (
                <ListFilter
                    size={15}
                    style={{ color: 'var(--color-grey-dark)' }}
                />
            );
        }
    }, [hasActiveState]);

    return (
        <div
            ref={headerRef}
            className="tableHeader-mainContainer"
        >
            <>
                <div style={{ width: `${totalWidth}px`, display: 'flex' }}>
                    {selectableRows && (
                        <div 
                            className="tableHeader-checkboxHeader"
                            style={{ width: `${checkboxColumnWidth}px` }}
                        >
                            {hasSelectedRows && (
                                <Checkbox
                                    size={16}
                                    style={'minus'}
                                    checked={hasSelectedRows}
                                    onChange={() => onSelectAll?.()} 
                                />
                            )}
                        </div>
                    )}
                    
                    {columnConfigs.map((config, index) => {
                        const cellWidth = {
                            width: `${columnWidths[index]}px`,
                            minWidth: `${minWidth}px`
                        };
                        
                        return (
                            <div
                                key={config.colName}
                                className={`tableHeader-cellContainer ${enableFilters ? 'tableHeader-cellContainer--filterable' : ''}`}
                                style={cellWidth}
                                onClick={enableFilters ? (e) => handleFilterClick(e, config.colName) : undefined}
                            >   
                                <span className="tableHeader-headerText">
                                    {config.colHeader || config.colName}
                                </span>
                                {enableFilters && getColumnIcon(config.colName)}
                            </div>
                        );
                    })}
                </div>
            </>
            <TableFilter
                columnConfigs={columnConfigs}
                jsonColumnConfig={jsonColumnConfig}
                isOpen={filterOpen}
                position={filterPosition}
                onClose={() => setFilterOpen(false)}
                data={getFilteredDataForColumn(activeColumnName)}
                originalData={data}
                columnName={activeColumnName}
                onFilterApply={handleFilterApply}
                initialCheckedValues={activeFilters[activeColumnName] || new Set()}
                onSortApply={handleSortApply}
                currentSortOrder={activeSorts[activeColumnName] || 'none'}
                userData={userData}
                tableName={tableName}
            />
        </div>
    );
};

export default React.memo(TableHeader, (prevProps, nextProps) => {
    return (
        prevProps.hasSelectedRows === nextProps.hasSelectedRows &&
        prevProps.columnWidths === nextProps.columnWidths &&
        prevProps.data === nextProps.data &&
        prevProps.enableFilters === nextProps.enableFilters &&
        prevProps.resetActionsRef === nextProps.resetActionsRef &&
        prevProps.selectableRows === nextProps.selectableRows
    );
});